/** Per-subsidiary brand metadata for OG tags, manifest, and HTML head injection. */

export interface BrandMeta {
  appName: string;
  shortName: string;
  description: string;
  ogTitle: string;
  ogDescription: string;
  themeColor: string;
  backgroundColor: string;
  favicon: string;
  ogImage: string;
  appleTouchIcon: string;
  noscriptHtml: string;
  /** The brand's canvassing app: App Store ids are in apps/mobile/eas.json, Play package names in apps/mobile/app.config.js. */
  appStoreUrl: string;
  playStoreUrl: string;
}

export const brandMeta: Record<string, BrandMeta> = {
  gop: {
    appStoreUrl: 'https://apps.apple.com/app/id6761330757',
    playStoreUrl: 'https://play.google.com/store/apps/details?id=com.signalvote.operations',
    appName: 'SignalVote',
    shortName: 'SignalVote',
    description: 'The command center for campaign execution.',
    ogTitle: 'SignalVote — The Command Center for Campaign Execution',
    ogDescription:
      'Track field activity, surface donor opportunities, and stay ahead of compliance without juggling spreadsheets.',
    themeColor: '#b71c1c',
    backgroundColor: '#fafafa',
    favicon: '/brands/gop/favicon.svg',
    ogImage: '/brands/gop/logo.png',
    appleTouchIcon: '/brands/gop/icon.png',
    noscriptHtml: `
      <h1>SignalVote — The Command Center for Campaign Execution</h1>
      <p>SignalVote is a campaign management platform by SignalVote, LLC that helps political campaigns organize canvassing operations, manage donor outreach, track financial compliance, and coordinate staff — all from one system.</p>
      <h2>Features</h2>
      <ul>
        <li>Canvassing operations — cut turf, assign routes, track door knocks in real time</li>
        <li>Donor intelligence — surface likely contributors and prioritize outreach</li>
        <li>Financial compliance — track FEC filing deadlines and generate reports</li>
        <li>Calendar management — sync shifts, events, and deadlines</li>
        <li>Staff management — assign roles, track accountability, coordinate teams</li>
      </ul>
      <h2>Pricing</h2>
      <p>Founding Campaign: $200/month — full platform access including canvassing, financial compliance, donor intelligence, calendar management, and staff management. Cancel anytime.</p>
      <h2>Contact</h2>
      <p>Email: support@signalvote.com</p>
      <p>SignalVote, LLC — 108 Lakeland Ave, Dover, DE 19901</p>
      <h2>Legal</h2>
      <p><a href="/terms">Terms of Service</a> | <a href="/privacy-policy">Privacy Policy</a> | <a href="/support">Support</a></p>
    `,
  },
  democrat: {
    appStoreUrl: 'https://apps.apple.com/app/id6759428483',
    playStoreUrl: 'https://play.google.com/store/apps/details?id=com.votecivic.operations',
    appName: 'VoteCivic',
    shortName: 'VoteCivic',
    description: 'Campaign command center for grassroots campaigns.',
    ogTitle: 'VoteCivic — Grassroots Power, Modern Tools.',
    ogDescription: 'One platform for field operations, scheduling, donor intelligence, and FEC compliance.',
    themeColor: '#1565c0',
    backgroundColor: '#fafafa',
    favicon: '/brands/democrat/favicon.svg',
    ogImage: '/brands/democrat/logo.svg',
    appleTouchIcon: '/brands/democrat/icon.png',
    noscriptHtml: `
      <h1>VoteCivic — Grassroots Power, Modern Tools</h1>
      <p>VoteCivic is a campaign command center for grassroots campaigns. Unify field ops, donor discovery, and financial compliance in one platform.</p>
      <p><a href="/terms">Terms of Service</a> | <a href="/privacy-policy">Privacy Policy</a> | <a href="/support">Support</a></p>
    `,
  },
  nonpartisan: {
    // Platform emails already brand nonpartisan as Veritas Assembly (CampaignBranding.SubsidiaryBrandNames),
    // but there is no Veritas Assembly app yet, so its volunteers get Vote Civic's. When one ships:
    // appStoreUrl: 'https://apps.apple.com/app/id<veritas-asc-app-id>',
    // playStoreUrl: 'https://play.google.com/store/apps/details?id=<veritas-android-package>',
    appStoreUrl: 'https://apps.apple.com/app/id6759428483',
    playStoreUrl: 'https://play.google.com/store/apps/details?id=com.votecivic.operations',
    appName: 'VoteCivic',
    shortName: 'VoteCivic',
    description: 'Stop the chaos. Win the race.',
    ogTitle: 'VoteCivic — Stop the Chaos. Win the Race.',
    ogDescription:
      'One platform for field operations, scheduling, donor intelligence, and FEC compliance.',
    themeColor: '#673de6',
    backgroundColor: '#f2f3f6',
    favicon: '/brands/nonpartisan/favicon.svg',
    ogImage: '/brands/nonpartisan/logo.svg',
    appleTouchIcon: '/brands/nonpartisan/logo.svg',
    noscriptHtml: `
      <h1>VoteCivic — Stop the Chaos. Win the Race.</h1>
      <p>VoteCivic is a campaign management platform for field operations, scheduling, donor intelligence, and FEC compliance. One system to run your entire campaign.</p>
      <p><a href="/terms">Terms of Service</a> | <a href="/privacy-policy">Privacy Policy</a> | <a href="/support">Support</a></p>
    `,
  },
};

export function getBrandMeta(subsidiary: string): BrandMeta {
  return brandMeta[subsidiary] ?? brandMeta.nonpartisan;
}
